import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import {
  AccumulationWhenCell,
  GrowthValueCell,
  ScheduleGrowthTooltip,
} from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { currency } from '../formatters';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { reimbursementPayoutRows } from './payoutBreakdown';
import type { StrategyPresentation } from './types';

const DEFAULT_COB_MONTHS = 24;

interface IndexedAnnuityCareLikeDeltas {
  baseBenefitDays?: number;
  cobBenefitDays?: number;
  baseMonthlyLimit?: number;
  cobMonthlyLimit?: number;
  accountValueReduction?: number;
}

function strategyDeltas(month: MonthlyEntry): IndexedAnnuityCareLikeDeltas {
  return (month.deltas.strategyDeltas ?? {}) as IndexedAnnuityCareLikeDeltas;
}

function ScheduleLimitTooltip({
  title,
  scheduledPool,
  durationMonths,
  monthlyCap,
  monthlyCapLabel,
  note,
}: {
  title: string;
  scheduledPool?: number;
  durationMonths?: number;
  monthlyCap: number;
  monthlyCapLabel: string;
  note: string;
}) {
  const showDurationMath = scheduledPool != null && durationMonths != null;

  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">{title}</div>
      <div className="mb-1 text-[10px] text-gray-300">{note}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        {showDurationMath && (
          <>
            <span className="text-gray-300">Scheduled pool</span>
            <span className="font-semibold tabular-nums">{currency(scheduledPool)}</span>
            <span className="text-gray-300">Benefit period</span>
            <span className="font-semibold tabular-nums">{durationMonths} mo</span>
          </>
        )}
        <span className="text-gray-300">Monthly cap</span>
        {showDurationMath ? (
          <span className="font-semibold tabular-nums">
            {currency(scheduledPool)} / {durationMonths} = {currency(monthlyCap)}
          </span>
        ) : (
          <span className="font-semibold tabular-nums">
            {monthlyCapLabel}: {currency(monthlyCap)}
          </span>
        )}
        <span className="text-gray-300">Daily equivalent</span>
        <span className="font-semibold tabular-nums">
          {currency(monthlyCap / DAYS_IN_MONTH)}/day
        </span>
      </div>
    </TooltipContent>
  );
}

function IndexedAnnuityCarePoolLimitCell({
  snapshot,
  terms,
}: {
  snapshot: YearSnapshot | undefined;
  terms: ClaimTerms;
}) {
  if (!snapshot) return <span className="text-gray-300">-</span>;

  const policyYear = Math.floor(snapshot.policyYear);
  const basePool = snapshot.effectiveFaceAmount;
  const cobPool = snapshot.effectiveContinuationBenefit ?? 0;
  const total = snapshot.theoreticalLtcPoolCeiling;
  const cobMonths = terms.extensionOfBenefitsPoolDurationMonths ?? DEFAULT_COB_MONTHS;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help leading-tight">
          <div className="font-medium text-gray-900">{currency(total)}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Guaranteed LTC pool - policy year {policyYear}</div>
        <div className="mb-1 text-[10px] text-gray-300">
          Extracted Guaranteed Rates LTC Benefit Balance plus inflated COB balance before actual
          claim drawdown.
        </div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="col-span-2 mt-1 text-[10px] uppercase tracking-wide text-gray-400">
            Base annuity LTCBB
          </span>
          <span className="text-gray-300">Guaranteed LTCBB</span>
          <span className="font-semibold tabular-nums">{currency(basePool)}</span>
          <span className="text-gray-300">Monthly limit</span>
          <span className="font-semibold tabular-nums">extracted row limit</span>
          <span className="col-span-2 mt-1 text-[10px] uppercase tracking-wide text-gray-400">
            COB rider
          </span>
          <span className="text-gray-300">Scheduled COB</span>
          <span className="font-semibold tabular-nums">{currency(cobPool)}</span>
          <span className="text-gray-300">Monthly limit</span>
          <span className="font-semibold tabular-nums">
            {currency(cobPool)} / {cobMonths} = {currency(cobPool / cobMonths)}
          </span>
          <span className="mt-1 font-semibold text-gray-200">Total guaranteed LTC</span>
          <span className="mt-1 font-semibold tabular-nums">{currency(total)}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function buildIndexedAnnuityCareAccumulationColumns({
  terms,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const baseAtIssue = terms.baseBenefitPoolAtIssue ?? 0;
  const cobAtIssue = terms.extensionOfBenefitsPoolAtIssue ?? 0;
  const cobMonths = terms.extensionOfBenefitsPoolDurationMonths ?? DEFAULT_COB_MONTHS;

  return [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
    {
      key: 'base-ltcbb',
      label: 'Base LTCBB',
      tooltip: (
        <HeaderTooltip
          description="The base annuity LTC Benefit Balance before benefits are used. This value comes from the extracted Guaranteed Rates row and is flat across the before-care timeline."
          formulas={[{ label: 'Output', expression: 'extracted Guaranteed Rates LTCBB' }]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={
            <ScheduleGrowthTooltip
              startingLabel={`At issue (${terms.policyPremiumStartYear})`}
              startingValue={baseAtIssue}
              result={row.snapshot.effectiveFaceAmount}
              driverLabel="Extracted Guaranteed Rates LTCBB"
            />
          }
        />
      ),
    },
    {
      key: 'cob-balance',
      label: 'COB balance',
      tooltip: (
        <HeaderTooltip
          description="The Continuation of Benefits rider balance for this policy year before benefits are used."
          formulas={[
            {
              label: 'Output',
              expression: (
                <>
                  COB issue balance x{' '}
                  <GrowthFactor
                    rate="COB inflation rate"
                    exponent={
                      terms.extensionOfBenefitsPoolInflationCapYears
                        ? `min(completed policy years, ${terms.extensionOfBenefitsPoolInflationCapYears})`
                        : 'completed policy years'
                    }
                  />
                </>
              ),
            },
          ]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveContinuationBenefit}
          prevValue={prev?.snapshot.effectiveContinuationBenefit ?? null}
          tooltip={
            <ScheduleGrowthTooltip
              startingLabel={`At issue (${terms.policyPremiumStartYear})`}
              startingValue={cobAtIssue}
              result={row.snapshot.effectiveContinuationBenefit}
              driverLabel="COB rider inflation schedule"
            />
          }
        />
      ),
    },
    {
      key: 'scheduled-ltc',
      label: 'Scheduled LTC',
      tooltip: (
        <HeaderTooltip
          description="The total scheduled long-term-care balance before benefits are used."
          formulas={[{ label: 'Output', expression: 'Base LTCBB + COB balance' }]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.theoreticalLtcPoolCeiling}
          prevValue={prev?.snapshot.theoreticalLtcPoolCeiling ?? null}
          tooltip={
            <ScheduleGrowthTooltip
              startingLabel={`At issue (${terms.policyPremiumStartYear})`}
              startingValue={baseAtIssue + cobAtIssue}
              result={row.snapshot.theoreticalLtcPoolCeiling}
              driverLabel="Base LTCBB + COB balance"
              rows={[
                ['Base LTCBB', row.snapshot.effectiveFaceAmount],
                ['COB balance', row.snapshot.effectiveContinuationBenefit],
              ]}
            />
          }
        />
      ),
    },
    {
      key: 'monthly-limit',
      label: 'Monthly limit',
      tooltip: (
        <HeaderTooltip
          description="The monthly LTC limit before care begins. The base limit comes from the extracted Guaranteed Rates row; after base exhaustion, COB uses the COB balance divided by the COB benefit period."
          formulas={[
            { label: 'Base phase', expression: 'extracted Guaranteed Rates monthly limit' },
            { label: 'COB phase', expression: `COB balance / ${cobMonths} mo` },
          ]}
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={
            <ScheduleGrowthTooltip
              startingLabel={`At issue (${terms.policyPremiumStartYear})`}
              startingValue={row.snapshot.monthlyBenefitCap}
              result={row.snapshot.monthlyBenefitCap}
              driverLabel="Extracted Guaranteed Rates monthly limit"
            />
          }
        />
      ),
    },
  ];
}

export const indexedAnnuityCarePresentation: StrategyPresentation = {
  accumulationColumns: buildIndexedAnnuityCareAccumulationColumns,

  benefitAmountCell: ({ month, terms }) => {
    if (month.events.ineligibilityReason === 'pre-policy') return null;

    const sd = strategyDeltas(month);
    const fromCob = month.facts.appliedCapPoolSource === 'eob';
    const monthlyCap =
      (fromCob ? sd.cobMonthlyLimit : sd.baseMonthlyLimit) ?? month.facts.appliedMonthlyBenefitCap;
    const poolLabel = fromCob ? 'COB' : 'Base LTCBB';
    const durationMonths = fromCob
      ? (terms.extensionOfBenefitsPoolDurationMonths ?? DEFAULT_COB_MONTHS)
      : undefined;
    const scheduledPool = durationMonths != null ? monthlyCap * durationMonths : undefined;
    const note = fromCob
      ? 'COB monthly limit uses the rider balance divided by the COB benefit period.'
      : 'Base monthly limit is extracted from the same Guaranteed Rates row as the LTC Benefit Balance.';
    const monthlyCapLabel = fromCob ? 'COB balance / period' : 'Extracted row limit';

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <ScheduleLimitTooltip
          title={`Policy year ${Math.floor(month.context.policyYear)} - ${poolLabel} monthly limit`}
          scheduledPool={scheduledPool}
          durationMonths={durationMonths}
          monthlyCap={monthlyCap}
          monthlyCapLabel={monthlyCapLabel}
          note={note}
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="Indexed Annuity Care pays from the base annuity LTC Benefit Balance first, then the COB rider after the base pool reaches $0."
        sections={[
          {
            heading: 'Base annuity LTCBB',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Scheduled accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
          },
          {
            heading: 'COB rider',
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? 'The residual Account Value was depleted to $0 by base LTCBB withdrawals.'
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Residual Account Value"
        description="The annuity Account Value is reduced by base LTCBB withdrawals only. COB rider withdrawals do not reduce Account Value."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced by base LTCBB draw', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // Keep the tooltip alive on a fully-ineligible month (waiting period / <2
    // ADL) when room & board is still out-of-pocket — Independent Living books
    // R&B even on non-paying months (cmTwo guards the same way); it's the exact
    // OOP this modeling exists to surface. Otherwise PaidCell shows the
    // zero-row reason natively.
    if (eligibleDays === 0 && (month.facts.roomAndBoardCost ?? 0) === 0) return null;

    const sd = strategyDeltas(month);
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const baseDays = sd.baseBenefitDays ?? 0;
    const cobDays = sd.cobBenefitDays ?? 0;
    const baseLimit = sd.baseMonthlyLimit ?? 0;
    const cobLimit = sd.cobMonthlyLimit ?? 0;

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout - reimbursement</div>
        <div className="mb-1 text-[10px] text-gray-300">
          Reimburses eligible incurred cost up to the active pool's daily cap. Base LTCBB pays
          first; COB pays only after base exhaustion.
        </div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {reimbursementPayoutRows({ month })}
        </div>
        {drawnBase > 0 || drawnEob > 0 ? (
          <div className="mt-1 border-t border-gray-700 pt-1">
            <div className="mb-0.5 text-[10px] text-gray-400">Pool draw this month</div>
            <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
              {drawnBase > 0 && (
                <>
                  <span className="text-gray-300">Base LTCBB</span>
                  <span className="font-semibold tabular-nums">
                    {currency(drawnBase)} ({baseDays.toFixed(1)}d at {currency(baseLimit)}/mo)
                  </span>
                </>
              )}
              {drawnEob > 0 && (
                <>
                  <span className="text-gray-300">COB rider</span>
                  <span className="font-semibold tabular-nums">
                    {currency(drawnEob)} ({cobDays.toFixed(1)}d at {currency(cobLimit)}/mo)
                  </span>
                </>
              )}
            </div>
          </div>
        ) : null}
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const sd = strategyDeltas(month);
    const baseDays = sd.baseBenefitDays ?? 0;
    const cobDays = sd.cobBenefitDays ?? 0;
    if (baseDays > 0 && cobDays > 0) {
      return (
        <div className="mt-0.5 text-[10px] text-gray-500">
          {baseDays.toFixed(1)}d base + {cobDays.toFixed(1)}d COB
        </div>
      );
    }

    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;
    if (!(wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0)) return null;

    return (
      <div className="mt-0.5 text-[10px] text-amber-700">
        {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
      </div>
    );
  },

  poolLimitCell: ({ snapshot, terms }) => (
    <IndexedAnnuityCarePoolLimitCell
      snapshot={snapshot}
      terms={terms}
    />
  ),

  drawnCellLabels: {
    description:
      'Cumulative reduction of the Indexed Annuity Care LTC pools. Base LTCBB is drawn first; COB is drawn after base exhaustion.',
    baseLabel: 'Base LTCBB drawn',
    cobLabel: 'COB drawn',
  },
  columnLabels: {
    'benefit-amount': 'Monthly limit',
    'pool-limit': 'Scheduled LTC',
    'death-benefit': 'Residual AV',
  },
  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. Indexed Annuity Care draws from the base annuity LTC Benefit Balance first, then the COB rider after base exhaustion."
        formulas={[
          {
            label: 'Closing pool',
            expression:
              '(opening base LTCBB + opening COB) + COB inflation accrual - drawn this month',
          },
          { label: 'Draw order', expression: 'base LTCBB first; COB after base reaches $0' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy reimbursement this month, with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Reimbursement',
            expression:
              'min(eligible cost, active daily limit, pool remaining), base first then COB',
          },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase and environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost x{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost x days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly LTC limit for this row. While the base pool has balance the limit is the extracted Guaranteed Rates LTC Monthly Withdrawal Limit; after base exhaustion the limit switches to scheduled COB divided by the COB benefit period."
        formulas={[
          { label: 'Base phase', expression: 'extracted Guaranteed Rates monthly limit' },
          { label: 'COB phase', expression: 'scheduled COB / COB benefit-period months' },
        ]}
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Extracted guaranteed base LTCBB plus COB pool size at this policy year before actual claim drawdown."
        formulas={[
          { label: 'Base LTCBB', expression: 'extracted Guaranteed Rates LTC Benefit Balance' },
          { label: 'COB', expression: 'COB issue balance x rider inflation factor' },
          { label: 'Total', expression: 'base LTCBB + COB' },
        ]}
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the base LTCBB and COB pools through this month."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + sum(base depletion + COB depletion)' },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month residual Account Value. Base LTCBB withdrawals reduce Account Value; COB withdrawals do not."
        formulas={[{ label: 'Residual', expression: 'max(0, Account Value - base LTCBB draws)' }]}
        notes="The first pass uses dollar-for-dollar AV reduction pending carrier validation of the proportional formula."
      />
    ),
  },
};
